'use client'

// Third-party Imports
import { useEffect, useState } from 'react'

import classnames from 'classnames'

// Type Imports

import type { ChildrenType } from '@core/types'

// Config Imports
import themeConfig from '@configs/themeConfig'

// Hook Imports
import { useSettings } from '@core/hooks/useSettings'

// Util Imports
import { verticalLayoutClasses } from '@layouts/utils/layoutClasses'

// Styled Component Imports
import StyledMain from '@layouts/styles/shared/StyledMain'

const LayoutContent = ({ children }: ChildrenType) => {
  // Hooks
  const { settings } = useSettings()
  const [keyboardInset, setKeyboardInset] = useState(0)

  // Vars
  const contentCompact = settings.contentWidth === 'compact'
  const contentWide = settings.contentWidth === 'wide'

  useEffect(() => {
    const vv = window.visualViewport
    const root = document.documentElement
    let baselineHeight = Math.round(vv?.height ?? window.innerHeight)

    const isTextField = (el: Element | null): el is HTMLInputElement | HTMLTextAreaElement => {
      return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
    }

    const isIgnoredInputType = (el: HTMLInputElement | HTMLTextAreaElement) => {
      if (el instanceof HTMLTextAreaElement) {
        return false
      }

      return ['button', 'submit', 'checkbox', 'radio', 'file'].includes(el.type)
    }

    const updateViewportVars = () => {
      const height = Math.round(vv?.height ?? window.innerHeight)

      root.style.setProperty('--ts-vvh', `${height}px`)
    }

    const updateKeyboardInset = () => {
      const active = document.activeElement

      if (!vv || !isTextField(active) || isIgnoredInputType(active)) {
        baselineHeight = Math.round(vv?.height ?? window.innerHeight)
        setKeyboardInset(0)

        return
      }

      const inset = Math.max(0, baselineHeight - vv.height - vv.offsetTop)

      setKeyboardInset(Math.round(inset))
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement

      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
        return
      }

      const isQuote = e.key === "'" || e.key === '"'

      if (isQuote) {
        e.preventDefault()
      }
    }

    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement

      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
        return
      }

      const hasQuotes = /['"]/.test(target.value)

      if (hasQuotes) {
        target.value = target.value.replace(/['"]/g, '')
      }
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement

      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
        return
      }

      if (isIgnoredInputType(target)) {
        return
      }

      if (target.dataset.autoSelect === 'false') {
        return
      }

      target.select()

      updateViewportVars()
      updateKeyboardInset()

      if (window.matchMedia('(max-width: 900px)').matches) {
        requestAnimationFrame(() => {
          target.scrollIntoView({ block: 'center', inline: 'nearest' })
        })
        setTimeout(() => {
          target.scrollIntoView({ block: 'center', inline: 'nearest' })
        }, 250)
      }
    }

    const handleFocusOut = () => {
      requestAnimationFrame(() => {
        updateViewportVars()
        updateKeyboardInset()
      })
    }

    // Satu kali attach semua
    updateViewportVars()
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('input', handleInput)
    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)

    vv?.addEventListener('resize', updateKeyboardInset)
    vv?.addEventListener('scroll', updateKeyboardInset)
    vv?.addEventListener('resize', updateViewportVars)
    vv?.addEventListener('scroll', updateViewportVars)
    window.addEventListener('resize', updateViewportVars)
    window.addEventListener('resize', updateKeyboardInset)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('input', handleInput)
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
      vv?.removeEventListener('resize', updateKeyboardInset)
      vv?.removeEventListener('scroll', updateKeyboardInset)
      vv?.removeEventListener('resize', updateViewportVars)
      vv?.removeEventListener('scroll', updateViewportVars)
      window.removeEventListener('resize', updateViewportVars)
      window.removeEventListener('resize', updateKeyboardInset)
      root.style.removeProperty('--ts-vvh')
    }
  }, [])

  return (
    <StyledMain
      isContentCompact={contentCompact}
      className={classnames(verticalLayoutClasses.content, 'flex-auto', {
        [`${verticalLayoutClasses.contentCompact} is-full`]: contentCompact,
        [verticalLayoutClasses.contentWide]: contentWide
      })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
        paddingBottom: themeConfig.layoutPadding + keyboardInset
      }}
    >
      {children}
    </StyledMain>
  )
}

export default LayoutContent
